import { useNavigate } from "react-router-dom";

import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import React, { useEffect } from "react";
import { Cancel } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { useSnackbar } from "notistack";
import {
  createProductProcess,
  getProductList,
} from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import { PRODUCTS_PROCESS } from "app/routes/constant";

const ProductProcessAdd = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const supplierData = useSelector((state) => state?.supplier);

  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    products: Yup.array().of(
      Yup.object({
        date: Yup.date().required("Date is required"),
        supplierId: Yup.object().nullable().required("Supplier is required"),
        challanNumber: Yup.string().required("Challan number is required"),
        lotNo: Yup.string().required("Lot number is required"),
        millName: Yup.string().required("Mill name is required"),
        productId: Yup.object().nullable().required("The product is required"),
        colors: Yup.array()
          .of(
            Yup.object({
              variantId: Yup.object()
                .nullable()
                .required("The variant is required"),
              quantity: Yup.number()
                .nullable()
                .typeError("Quantity must be a number")
                .required("Please enter quantity")
                .min(1, "Quantity cannot be 0 or negative")
                .integer("Quantity must be a whole number"),
            }),
          )
          .min(1, "At least one color is required"),
      }),
    ),
  });

  useEffect(() => {
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVariantList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Knitter" }));
  }, [dispatch]);

  return (
    <>
      <Card>
        <CardHeader title={`Add Product Process`} />
        <CardContent>
          <Formik
            initialValues={{
              products: [
                {
                  date: new Date().toISOString().split("T")[0],
                  supplierId: null,
                  challanNumber: "",
                  productId: null,
                  millName: "",
                  lotNo: "",
                  colors: [
                    {
                      variantId: null,
                      quantity: 0,
                    },
                  ],
                },
              ],
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={async (data, { setSubmitting }) => {
              const productArr = {
                items: data.products?.flatMap((val) =>
                  val.colors.map((color) => ({
                    date: val.date,
                    supplierId: val?.supplierId?._id,
                    challanNumber: val.challanNumber,
                    productId: val?.productId?._id,
                    variantId: color?.variantId?._id,
                    quantity: color.quantity,
                    millName: val?.millName,
                    lotNo: val?.lotNo.trim(),
                  })),
                ),
              };

              setSubmitting(true);
              const response = await dispatch(
                createProductProcess(productArr),
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(PRODUCTS_PROCESS);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({ values, errors, isSubmitting, setFieldValue }) => (
              <Form>
                <div>
                  <Grid container spacing={3} mb={3}></Grid>
                  <div>
                    <FieldArray name={`products`}>
                      {({ remove: removeVariant, push: pushVariant }) => (
                        <div>
                          {values?.products?.length > 0 &&
                            values?.products?.map((valVariant, index) => (
                              <div key={index}>
                                <Grid container spacing={3} mb={3}>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      sx={{ width: "100%" }}
                                      name={`products[${index}].date`}
                                      type="date"
                                      label="Date*"
                                      InputLabelProps={{ shrink: true }}
                                      value={valVariant?.date}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `products[${index}].date`,
                                          e.target.value,
                                        )
                                      }
                                      error={
                                        errors?.products?.length &&
                                        errors?.products[index]?.date
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.date
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3.5}>
                                    <JumboAutoComplete
                                      options={supplierData.list ?? []}
                                      label="Supplier*"
                                      size="large"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].supplierId`}
                                      value={valVariant?.supplierId}
                                      onChange={(evt, value) => {
                                        setFieldValue(
                                          `products[${index}].supplierId`,
                                          value,
                                        );
                                      }}
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.supplierId
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2.5}>
                                    <TextField
                                      sx={{ width: "100%" }}
                                      name={`products[${index}].challanNumber`}
                                      label="Challan Number*"
                                      size="large"
                                      value={valVariant?.challanNumber}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `products[${index}].challanNumber`,
                                          e.target.value,
                                        )
                                      }
                                      error={
                                        errors?.products?.length &&
                                        errors?.products[index]?.challanNumber
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.challanNumber
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      sx={{ width: "100%" }}
                                      name={`products[${index}].lotNo`}
                                      label="Lot No"
                                      size="large"
                                      onChange={(e) =>
                                        setFieldValue(
                                          `products[${index}].lotNo`,
                                          e.target.value,
                                        )
                                      }
                                      value={valVariant?.lotNo}
                                      error={
                                        errors?.products?.length &&
                                        errors?.products[index]?.lotNo
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.lotNo
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3.5}>
                                    <JumboAutoComplete
                                      options={productData.list ?? []}
                                      label="Product*"
                                      size="large"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].productId`}
                                      value={valVariant?.productId}
                                      onChange={(evt, value) => {
                                        setFieldValue(
                                          `products[${index}].productId`,
                                          value,
                                        );
                                        // Reset all colors when product changes
                                        setFieldValue(
                                          `products[${index}].colors`,
                                          [{ variantId: null, quantity: 0 }],
                                        );
                                      }}
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.productId
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      sx={{ width: "100%" }}
                                      name={`products[${index}].millName`}
                                      label="Mill Name"
                                      size="large"
                                      onChange={(e) =>
                                        setFieldValue(
                                          `products[${index}].millName`,
                                          e.target.value,
                                        )
                                      }
                                      value={valVariant?.millName}
                                      error={
                                        errors?.products?.length &&
                                        errors?.products[index]?.millName
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products[index]?.millName
                                      }
                                    />
                                  </Grid>

                                  {index !== 0 && (
                                    <Grid item xs={12} sm={1}>
                                      <IconButton
                                        size="large"
                                        onClick={() => {
                                          removeVariant(index);
                                        }}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    </Grid>
                                  )}
                                  {index === 0 && (
                                    <Grid item xs={12} sm={1.5}>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ mt: 1.5 }}
                                        onClick={() =>
                                          pushVariant({
                                            productId: null,
                                            millName: "",
                                            lotNo: "",
                                            date: new Date()
                                              .toISOString()
                                              .split("T")[0],
                                            supplierId: null,
                                            challanNumber: "",
                                            colors: [
                                              {
                                                variantId: null,
                                                quantity: 0,
                                              },
                                            ],
                                          })
                                        }
                                      >
                                        Add More
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Colors Section */}
                                <FieldArray name={`products[${index}].colors`}>
                                  {({
                                    remove: removeColor,
                                    push: pushColor,
                                  }) => (
                                    <div
                                      style={{
                                        marginLeft: "20px",
                                        marginBottom: "20px",
                                      }}
                                    >
                                      {valVariant?.colors?.map(
                                        (color, colorIndex) => (
                                          <Grid
                                            container
                                            spacing={3}
                                            mb={2}
                                            key={colorIndex}
                                          >
                                            <Grid item xs={12} sm={3.5}>
                                              <JumboAutoComplete
                                                options={
                                                  valVariant?.productId
                                                    ? variantData?.list.filter(
                                                        (list) =>
                                                          list?.product?._id ===
                                                          valVariant?.productId
                                                            ?._id,
                                                      ) || []
                                                    : []
                                                }
                                                label="Variant*"
                                                size="large"
                                                getOptionLabel={(option) =>
                                                  option?.name
                                                }
                                                name={`products[${index}].colors[${colorIndex}].variantId`}
                                                value={color?.variantId}
                                                onChange={(evt, value) =>
                                                  setFieldValue(
                                                    `products[${index}].colors[${colorIndex}].variantId`,
                                                    value,
                                                  )
                                                }
                                                helperText={
                                                  errors?.products?.length &&
                                                  errors?.products[index]
                                                    ?.colors?.[colorIndex]
                                                    ?.variantId
                                                }
                                              />
                                            </Grid>

                                            <Grid item xs={12} sm={2}>
                                              <TextField
                                                sx={{ width: "100%" }}
                                                name={`products[${index}].colors[${colorIndex}].quantity`}
                                                type="text"
                                                label="Qty*"
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `products[${index}].colors[${colorIndex}].quantity`,
                                                    +e.target.value,
                                                  );
                                                }}
                                                value={color?.quantity}
                                                error={
                                                  errors?.products?.length &&
                                                  errors?.products[index]
                                                    ?.colors?.[colorIndex]
                                                    ?.quantity
                                                }
                                                helperText={
                                                  errors?.products?.length &&
                                                  errors?.products[index]
                                                    ?.colors?.[colorIndex]
                                                    ?.quantity
                                                }
                                              />
                                            </Grid>

                                            {colorIndex !== 0 && (
                                              <Grid item xs={12} sm={1}>
                                                <IconButton
                                                  size="large"
                                                  onClick={() => {
                                                    removeColor(colorIndex);
                                                  }}
                                                >
                                                  <Cancel />
                                                </IconButton>
                                              </Grid>
                                            )}

                                            {colorIndex === 0 && (
                                              <Grid item xs={12} sm={2}>
                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  sx={{ mt: 1.5 }}
                                                  onClick={() =>
                                                    pushColor({
                                                      variantId: null,
                                                      quantity: 0,
                                                    })
                                                  }
                                                >
                                                  Add Color
                                                </Button>
                                              </Grid>
                                            )}
                                          </Grid>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </FieldArray>
                              </div>
                            ))}
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  <br></br>
                </div>
                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%" }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      navigate(PRODUCTS_PROCESS);
                    }}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default ProductProcessAdd;
